// LearnCore Frontend - 创建阅读材料页面

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import type {
  CreateReadingPassageRequest
} from '../types';
import {
  DocumentTextIcon,
  SparklesIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

export default function CreatePassagePage() {
  const navigate = useNavigate();

  // 基础信息
  const [title, setTitle] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [autoExtract, setAutoExtract] = useState(true); // 默认启用自动提取

  // UI状态
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 保存阅读材料
  const handleSave = async () => {
    if (!title.trim()) {
      setError('请输入标题');
      return;
    }

    if (!originalText.trim()) {
      setError('请输入原文');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const passageData: CreateReadingPassageRequest = {
        title: title.trim(),
        original_text: originalText.trim(),
        is_public: isPublic,
        auto_extract: autoExtract,
      };

      const response = await apiClient.createReadingPassage(passageData);

      if (response.passage) {
        // 如果启用了自动提取，跳转到列表页；否则跳转到编辑页
        if (autoExtract) {
          navigate('/passages?message=创建成功，正在后台提取内容，请稍后查看');
        } else {
          navigate(`/passages/${response.passage.id}/edit`);
        }
      }

    } catch (err: any) {
      setError(err.response?.data?.error || '保存失败，请稍后重试');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面头部 */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <DocumentTextIcon className="h-8 w-8 mr-3 text-primary-600" />
          创建阅读材料
        </h1>
        <p className="mt-2 text-gray-600">
          输入标题和英文原文，选择是否自动提取学习内容
        </p>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">操作失败</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-4">
                <button
                  onClick={() => setError(null)}
                  className="btn-outline text-sm"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {/* 基本信息卡片 */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
          <h2 className="text-lg font-medium text-gray-900">基本信息</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              标题 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              placeholder="例如：商务英语邮件写作"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              英文原文 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              placeholder="粘贴或输入英文原文段落..."
            />
          </div>

          {/* 选项 */}
          <div className="space-y-4 pt-4 border-t">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={autoExtract}
                onChange={(e) => setAutoExtract(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  <SparklesIcon className="h-4 w-4 inline mr-1 text-primary-600" />
                  自动智能提取
                </p>
                <p className="text-xs text-gray-500">
                  自动提取译文、重点词汇、语法考点等学习内容（在后台进行，稍后查看结果）
                </p>
              </div>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {isPublic ? (
                    <><EyeIcon className="h-4 w-4 inline mr-1" />公开分享</>
                  ) : (
                    <><EyeSlashIcon className="h-4 w-4 inline mr-1" />私有</>
                  )}
                </p>
                <p className="text-xs text-gray-500">
                  允许其他用户查看和学习这份材料
                </p>
              </div>
            </label>
          </div>

          {/* 操作按钮 */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={() => navigate('/passages')}
              className="btn-outline"
            >
              取消
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !title.trim() || !originalText.trim()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSaving ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  创建中...
                </>
              ) : (
                <>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  创建阅读材料
                </>
              )}
            </button>
          </div>
        </div>

        {/* 说明文字 */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">💡 使用提示</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>启用自动提取：</strong>创建后系统会在后台自动分析原文，提取译文、词汇、语法等内容，处理完成后可在列表中查看</li>
            <li>• <strong>不启用自动提取：</strong>创建后进入编辑页面，可以手动输入内容或点击"提取"按钮触发分析</li>
            <li>• 后台处理时间取决于原文长度，通常在1-2分钟内完成</li>
            <li>• 您可以在列表页面查看处理状态（处理中/已完成/失败）</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
